import { atom } from 'jotai';
import {
  CancelUnsubscribeParams,
  PendingUnsubscribe,
  ScheduleUnsubscribeParams,
} from '../types';
import { createSubscriptionKey, setDebugLabels } from '../utils/atomUtils';
import {
  UNSUBSCRIBE_DELAY_MS,
  clearExistingTimeout,
  addPendingUnsubscribe,
  removePendingUnsubscribe,
  clearAllTimeouts,
  executeForceUnsubscribe,
  executeDebouncedUnsubscribe,
  scheduleUnsubscribeTimeout,
  logScheduledUnsubscribe,
  logCancelledUnsubscribe,
  logFlushingUnsubscribes,
  flushAllPendingUnsubscribes,
} from '../utils/subscriptionUtils';

const pendingTimeoutsAtom = atom<Map<string, NodeJS.Timeout>>(new Map());

const pendingUnsubscribesAtom = atom<Map<string, PendingUnsubscribe>>(
  new Map(),
);

pendingTimeoutsAtom.onMount = (setAtom) => {
  return () => {
    let timeouts: Map<string, NodeJS.Timeout> = new Map();
    setAtom((prev) => {
      timeouts = prev;
      return new Map();
    });
    clearAllTimeouts(timeouts);
  };
};

export const scheduleDebouncedUnsubscribeAtom = atom(
  null,
  async (get, set, params: ScheduleUnsubscribeParams) => {
    const { topic, subscriberId, force = false } = params;
    const key = createSubscriptionKey(topic, subscriberId);

    const currentTimeouts = get(pendingTimeoutsAtom);
    const clearedTimeouts = clearExistingTimeout(key, currentTimeouts);
    if (clearedTimeouts !== currentTimeouts) {
      set(pendingTimeoutsAtom, clearedTimeouts);
    }

    if (force) {
      const currentPending = get(pendingUnsubscribesAtom);
      if (currentPending.has(key)) {
        const newPending = removePendingUnsubscribe(key, currentPending);
        set(pendingUnsubscribesAtom, newPending);
      }

      await executeForceUnsubscribe(set, topic, subscriberId);
      return;
    }

    const currentPending = get(pendingUnsubscribesAtom);
    const newPending = addPendingUnsubscribe(
      key,
      topic,
      subscriberId,
      currentPending,
    );
    set(pendingUnsubscribesAtom, newPending);

    logScheduledUnsubscribe(topic, subscriberId, UNSUBSCRIBE_DELAY_MS);

    const timeoutId = scheduleUnsubscribeTimeout(async () => {
      await executeDebouncedUnsubscribe(
        get,
        set,
        key,
        topic,
        subscriberId,
        pendingUnsubscribesAtom,
        pendingTimeoutsAtom,
      );
    }, UNSUBSCRIBE_DELAY_MS);

    const timeouts = get(pendingTimeoutsAtom);
    const updatedTimeouts = new Map(timeouts);
    updatedTimeouts.set(key, timeoutId);
    set(pendingTimeoutsAtom, updatedTimeouts);
  },
);

export const cancelDebouncedUnsubscribeAtom = atom(
  null,
  (get, set, params: CancelUnsubscribeParams): boolean => {
    const { topic, subscriberId } = params;
    const key = createSubscriptionKey(topic, subscriberId);
    const currentPending = get(pendingUnsubscribesAtom);

    if (!currentPending.has(key)) {
      return false;
    }

    const currentTimeouts = get(pendingTimeoutsAtom);
    const clearedTimeouts = clearExistingTimeout(key, currentTimeouts);
    if (clearedTimeouts !== currentTimeouts) {
      set(pendingTimeoutsAtom, clearedTimeouts);
    }

    const newPending = removePendingUnsubscribe(key, currentPending);
    set(pendingUnsubscribesAtom, newPending);

    logCancelledUnsubscribe(topic, subscriberId);

    return true;
  },
);

export const flushPendingUnsubscribesAtom = atom(null, async (get, set) => {
  const pendingUnsubscribes = get(pendingUnsubscribesAtom);

  if (pendingUnsubscribes.size === 0) {
    return;
  }

  logFlushingUnsubscribes(pendingUnsubscribes.size);

  const currentTimeouts = get(pendingTimeoutsAtom);
  clearAllTimeouts(currentTimeouts);
  set(pendingTimeoutsAtom, new Map());

  await flushAllPendingUnsubscribes(set, pendingUnsubscribes);

  set(pendingUnsubscribesAtom, new Map());
});

setDebugLabels({
  scheduleDebouncedUnsubscribeAtom,
  cancelDebouncedUnsubscribeAtom,
  flushPendingUnsubscribesAtom,
  pendingUnsubscribesAtom,
  pendingTimeoutsAtom,
});
